export type PermissionGroup =
  | "Knowledge"
  | "Clients & applications"
  | "Support tickets"
  | "Escalations"
  | "Assets"
  | "Training"
  | "Projects"
  | "Feedback"
  | "Administration";

export const PERMISSION_GROUPS: PermissionGroup[] = [
  "Knowledge",
  "Clients & applications",
  "Support tickets",
  "Escalations",
  "Assets",
  "Training",
  "Projects",
  "Feedback",
  "Administration",
];

export const PERMISSIONS = {
  // Knowledge
  "articles:view": { key: "articles:view", group: "Knowledge", label: "View articles", description: "Read knowledge base articles" },
  "articles:create": { key: "articles:create", group: "Knowledge", label: "Create articles", description: "Write new articles" },
  "articles:edit_any": { key: "articles:edit_any", group: "Knowledge", label: "Edit any article", description: "Edit articles created by others (owners can always edit their own)" },
  "articles:verify": { key: "articles:verify", group: "Knowledge", label: "Verify articles", description: "Mark an article as verified" },
  "articles:restore_version": { key: "articles:restore_version", group: "Knowledge", label: "Restore article versions", description: "Restore a previous version of an article" },
  "articles:moderate_comments": { key: "articles:moderate_comments", group: "Knowledge", label: "Moderate article comments", description: "Delete any comment under an article (everyone can already delete their own)" },
  "articles:delete": { key: "articles:delete", group: "Knowledge", label: "Delete articles", description: "Soft-delete and restore articles", dangerous: true },

  // Clients & applications
  "clients:view": { key: "clients:view", group: "Clients & applications", label: "View clients", description: "Read client information" },
  "clients:manage": { key: "clients:manage", group: "Clients & applications", label: "Manage clients", description: "Create and update clients" },
  "clients:archive": { key: "clients:archive", group: "Clients & applications", label: "Archive clients", description: "Archive and restore clients" },
  "clients:delete": { key: "clients:delete", group: "Clients & applications", label: "Delete clients", description: "Soft-delete and restore clients", dangerous: true },
  "applications:view": { key: "applications:view", group: "Clients & applications", label: "View applications", description: "Read application information" },
  "applications:manage": { key: "applications:manage", group: "Clients & applications", label: "Manage applications", description: "Create and update applications and submodules" },
  "applications:delete": { key: "applications:delete", group: "Clients & applications", label: "Delete applications", description: "Soft-delete and restore applications and submodules", dangerous: true },

  // Support tickets
  "tickets:view": { key: "tickets:view", group: "Support tickets", label: "View support tickets", description: "Read support tickets" },
  "tickets:create": { key: "tickets:create", group: "Support tickets", label: "Create support tickets", description: "Extract or create new support tickets" },
  "tickets:edit": { key: "tickets:edit", group: "Support tickets", label: "Edit support tickets", description: "Update and manage support tickets" },
  "tickets:delete": { key: "tickets:delete", group: "Support tickets", label: "Delete support tickets", description: "Soft-delete and restore support tickets", dangerous: true },

  // Escalations
  "escalations:view": { key: "escalations:view", group: "Escalations", label: "View escalations", description: "Read escalations" },
  "escalations:create": { key: "escalations:create", group: "Escalations", label: "Create escalations", description: "Create new escalations" },
  "escalations:edit_any": { key: "escalations:edit_any", group: "Escalations", label: "Edit any escalation", description: "Edit escalations owned by others (owners can always edit their own)" },
  "escalations:runbooks_manage": { key: "escalations:runbooks_manage", group: "Escalations", label: "Manage runbooks", description: "Create and manage runbook steps" },
  "escalations:settings_manage": { key: "escalations:settings_manage", group: "Escalations", label: "Manage escalation settings", description: "Change SLA targets and other settings" },
  "escalations:delete": { key: "escalations:delete", group: "Escalations", label: "Delete escalations", description: "Soft-delete and restore escalations", dangerous: true },

  // Assets
  "assets:view": { key: "assets:view", group: "Assets", label: "View assets", description: "View and stream uploaded assets" },
  "assets:upload": { key: "assets:upload", group: "Assets", label: "Upload assets", description: "Upload new assets and rename existing ones" },
  "assets:delete": { key: "assets:delete", group: "Assets", label: "Delete assets", description: "Archive and restore assets" },

  // Training
  "onboarding:view": { key: "onboarding:view", group: "Training", label: "View own training plan", description: "See your own training plan and its materials" },
  "onboarding:library_manage": { key: "onboarding:library_manage", group: "Training", label: "Manage library and programs", description: "Create and manage training library and programs" },
  "onboarding:own": { key: "onboarding:own", group: "Training", label: "Manage plans", description: "Assign and manage people's plans" },
  "onboarding:assess": { key: "onboarding:assess", group: "Training", label: "Score assessments", description: "Score assessments" },
  "training:credentials_manage": { key: "training:credentials_manage", group: "Training", label: "Manage credentials", description: "Create and grant training credentials" },
  "onboarding:insights": { key: "onboarding:insights", group: "Training", label: "View training insights", description: "View all training insights", dangerous: false },

  // Projects & Bexio
  "projects:view": { key: "projects:view", group: "Projects", label: "View projects", description: "View projects", scopable: "project" },
  "projects:manage": { key: "projects:manage", group: "Projects", label: "Manage projects", description: "Create and manage projects" },
  "projects:view_all": { key: "projects:view_all", group: "Projects", label: "View all projects", description: "Read-only access to every project, its board and time (management, sales)" },
  "projects:templates_manage": { key: "projects:templates_manage", group: "Projects", label: "Manage project templates", description: "Create, edit and duplicate project templates" },
  "projects:approve": { key: "projects:approve", group: "Projects", label: "Approve projects", description: "Create projects without approval and approve or reject projects drafted by PM managers" },
  "projects:create": { key: "projects:create", group: "Projects", label: "Create projects", description: "Create projects as drafts that wait for a PM admin's approval; manage their own projects" },
  "projects:start": { key: "projects:start", group: "Projects", label: "Start projects directly", description: "Create projects that start at once (Bexio budget created), without waiting for approval" },
  "pm:settings_manage": { key: "pm:settings_manage", group: "Projects", label: "Manage PM & Bexio settings", description: "Manage PM settings, user mappings, import wizard, and full sync", dangerous: true },
  "bexio:packages_manage": { key: "bexio:packages_manage", group: "Projects", label: "Manage Bexio packages", description: "Create Bexio projects and packages directly, change budgets, close packages" },
  "pm:contracts_manage": { key: "pm:contracts_manage", group: "Projects", label: "Manage dedicated hours", description: "Create and manage dedicated hours" },
  "time:log": { key: "time:log", group: "Projects", label: "Log time", description: "Log and edit own time entries and templates" },
  "time:recap": { key: "time:recap", group: "Projects", label: "Generate daily recap", description: "Generate a daily recap of own time entries to paste into Teams" },
  "time:view_all": { key: "time:view_all", group: "Projects", label: "View all time", description: "View all time entries across all projects" },
  "bexio:issues_view": { key: "bexio:issues_view", group: "Projects", label: "View time problems", description: "View Bexio time problems and logs" },
  "bexio:issues_manage": { key: "bexio:issues_manage", group: "Projects", label: "Manage time problems", description: "Resolve Bexio time problems and retry failed syncs" },
  "pm:money_view": { key: "pm:money_view", group: "Projects", label: "View money", description: "View financial rates and CHF amounts when enabled" },
  "pm:client_overview": { key: "pm:client_overview", group: "Projects", label: "View client overview", description: "See every client's packages, projects, tickets and articles on the client overview (people on a client's projects see their own projects without it)" },
  "pm:budget_view": { key: "pm:budget_view", group: "Projects", label: "View budget burn", description: "See budget, burn rate and forecast for every budget" },
  "pm:health_view": { key: "pm:health_view", group: "Projects", label: "View project health", description: "See late buckets, overdue cards, waiting time and upcoming go-lives" },
  "pm:digest_receive": { key: "pm:digest_receive", group: "Projects", label: "Receive the Monday PM digest", description: "Get the Monday morning summary of active projects, budgets, late buckets, over-budget projects and open time problems" },
  "reports:schedule": { key: "reports:schedule", group: "Projects", label: "Schedule reports", description: "Set up reports that build and email themselves" },
  "reports:view": { key: "reports:view", group: "Projects", label: "View and export reports", description: "Build time and value reports, export them to Excel and see the report history" },
  "timeoff:approve": { key: "timeoff:approve", group: "Projects", label: "Approve time off", description: "Approve or decline people's time-off requests and get the Monday time-off summary" },
  "pm:capacity_view": { key: "pm:capacity_view", group: "Projects", label: "View team capacity", description: "See how loaded each person is over the next 15 working days" },

  // Feedback
  "feedback:create": { key: "feedback:create", group: "Feedback", label: "Submit feedback", description: "Submit feedback on the portal" },
  "feedback:resolve": { key: "feedback:resolve", group: "Feedback", label: "Resolve feedback", description: "Mark feedback as resolved" },
  "feedback:delete": { key: "feedback:delete", group: "Feedback", label: "Delete feedback", description: "Soft-delete and restore feedback", dangerous: true },

  // Administration
  "users:view": { key: "users:view", group: "Administration", label: "View users", description: "Read user profiles and directory" },
  "users:approve": { key: "users:approve", group: "Administration", label: "Approve users", description: "Approve pending sign-ups" },
  "users:invite": { key: "users:invite", group: "Administration", label: "Invite users", description: "Invite outside people as Microsoft guests" },
  "users:manage": { key: "users:manage", group: "Administration", label: "Manage users", description: "Update, block, or delete users", dangerous: true },
  "access:manage": { key: "access:manage", group: "Administration", label: "Manage access", description: "Edit roles, role permissions, and direct grants", dangerous: true },
  "settings:manage": { key: "settings:manage", group: "Administration", label: "Manage settings", description: "Manage global portal settings" },
  "trash:restore": { key: "trash:restore", group: "Administration", label: "Restore deleted items", description: "Restore soft-deleted items from the trash" },
  "deletion_requests:review": { key: "deletion_requests:review", group: "Administration", label: "Review deletion requests", description: "Approve or dismiss deletion requests" },
  "audit:view": { key: "audit:view", group: "Administration", label: "View activity log", description: "Read and export the activity log (who changed what, sign-ins, exports)" },
  "audit:manage": { key: "audit:manage", group: "Administration", label: "Manage activity log retention", description: "Change how long the activity log is kept", dangerous: true },
  "notifications:manage": { key: "notifications:manage", group: "Administration", label: "Manage notifications for everyone", description: "Turn a type of notification off or on for everyone on eVia Hub", dangerous: true },
  "guests:settings_manage": { key: "guests:settings_manage", group: "Administration", label: "Manage guest settings", description: "Change the default access period for guests" },
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;

export function isPermissionKey(key: string): key is PermissionKey {
  return key in PERMISSIONS;
}

/** Any of these lets someone create a project; projects:approve and projects:start skip the draft. */
export const PROJECT_CREATE_PERMISSIONS = ["projects:approve", "projects:start", "projects:manage", "projects:create"] as const;

export const SYSTEM_ROLE_PERMISSIONS: Record<string, PermissionKey[]> = {
  super_admin: Object.keys(PERMISSIONS) as PermissionKey[], // wildcard evaluated in rules
  admin: [
    // member base (Any active user today + all view keys)
    "articles:view",
    "clients:view",
    "applications:view",
    "tickets:view",
    "escalations:view",
    "assets:view",
    "onboarding:view",
    "projects:view",
    "users:view",
    "audit:view",             // activity log
    // member active actions
    "clients:manage",      // createClient, updateClient
    "clients:archive",
    "applications:manage", // createApplication, updateApplication, createSubmodule
    "articles:create",     // createArticle, createTag
    "feedback:create",     // createFeedback
    "escalations:create",  // createEscalation, watch/unwatch, addEscalationNote, logEscalationInteraction, createArticleFromEscalation
    "tickets:create",      // createTicketFromExtraction
    "tickets:edit",        // updateTicket, markTicketDuplicate, promote, link, unlink, search, createArticleFromTicket
    "assets:upload",       // attachExistingAsset, renameAsset

    // admin-gated today
    "articles:edit_any",         // owner or admin
    "articles:restore_version",  // owner or admin
    "articles:verify",           // admin (markArticleVerified)
    "articles:delete",           // admin (restoreArticle, delete approvals)
    "articles:moderate_comments",// admin (delete any article comment)
    "clients:delete",            // admin (restoreClient, delete approvals)
    "applications:delete",       // admin (deleteSubmodule, restoreSubmodule, restoreApplication, delete approvals)
    "feedback:resolve",          // admin (resolveFeedback)
    "feedback:delete",           // admin (restoreFeedback, delete approvals)
    "escalations:edit_any",      // owner or admin (updateEscalation, updateEscalationResolution, etc.)
    "escalations:runbooks_manage",// admin (updateRunbook, upsertRunbookStep, etc.)
    "escalations:settings_manage",// no explicit guard mapped, assuming admin
    "escalations:delete",        // admin (restoreEscalation, delete approvals)
    "tickets:delete",            // admin (restoreTicket, delete approvals)
    "assets:delete",             // admin (deleteAssets, listDeletedAssets, restoreDeletedAssets)
    "trash:restore",             // admin (restoreClient, restoreApplication, etc.)
    "users:approve",             // admin (approveUser, rejectUser)
    "users:invite",              // admin (Invite outside people as Microsoft guests)
    "users:manage",              // admin (updateUser, deleteUser, blockUser, restoreUser, canManageUser)
    "settings:manage",           // admin (updateSettings)
    "guests:settings_manage",    // admin (updateGuestSettings)
    "notifications:manage",      // admin (org-wide notification switches, defaults and locks)
    "deletion_requests:review",  // admin (approve and dismiss)
    "onboarding:library_manage", // library + programs
    "onboarding:own",            // assign and manage people's plans
    "onboarding:assess",         // score assessments
    "training:credentials_manage",
    "onboarding:insights",       // all onboarding insights
    "projects:manage",           // assuming admin for future UI
    "projects:view_all",
    "projects:templates_manage",
    "projects:approve",
    "pm:settings_manage",
    "bexio:packages_manage",
    "pm:contracts_manage",
    "time:log",
    "time:recap",
    "time:view_all",
    "bexio:issues_view",
    "bexio:issues_manage",
    "pm:money_view",
    "pm:client_overview",
    "pm:budget_view",
    "pm:health_view",
    "pm:capacity_view",
    "pm:digest_receive",
    "timeoff:approve",
    "reports:view",
    "reports:schedule",
  ],
  // General manager (owner, 2026-11-03): runs the team — time off, the Team schedule, dedicated hours and project insights —
  // without the admin or project-editing rights.
  general_manager: [
    "articles:view",
    "clients:view",
    "applications:view",
    "tickets:view",
    "escalations:view",
    "assets:view",
    "onboarding:view",
    "projects:view",
    "projects:view_all",
    "users:view",
    "feedback:create",
    "time:log",
    "time:recap",
    "time:view_all",
    "timeoff:approve",
    "reports:view",
    "bexio:issues_view",
    "bexio:issues_manage",
    "pm:contracts_manage",
    "pm:capacity_view",
    "pm:budget_view",
    "pm:health_view",
    "pm:client_overview",
    "pm:money_view",
    "pm:digest_receive",
  ],
  member: [
    // all view keys
    "articles:view",
    "clients:view",
    "applications:view",
    "tickets:view",
    "escalations:view",
    "assets:view",
    "onboarding:view",
    "projects:view",
    "users:view",

    // active user actions
    "clients:manage",      // createClient, updateClient
    "clients:archive",
    "applications:manage", // createApplication, updateApplication, createSubmodule
    "articles:create",     // createArticle, createTag
    "feedback:create",     // createFeedback
    "escalations:create",  // createEscalation, watch/unwatch, addEscalationNote, logEscalationInteraction, createArticleFromEscalation
    "tickets:create",      // createTicketFromExtraction
    "tickets:edit",        // updateTicket, markTicketDuplicate, promote, link, unlink, search, createArticleFromTicket
    "assets:upload",       // attachExistingAsset, renameAsset
    "time:log",
    "time:recap",
    "projects:create",     // Junior SE / DAC: a new project waits for approval (owner, 2026-11-04)
  ],
  // PM manager (SPEC §9): create projects as drafts, edit their own, templates, client overview, budget burn,
  // health and capacity. Not: approving, money, settings, contracts, viewing every project, sync issues
  // (those actions are not scoped to a manager's projects yet).
  pm_manager: [
    "articles:view",
    "clients:view",
    "applications:view",
    "tickets:view",
    "escalations:view",
    "assets:view",
    "onboarding:view",
    "projects:view",
    "users:view",
    "clients:manage",
    "clients:archive",
    "applications:manage",
    "articles:create",
    "feedback:create",
    "escalations:create",
    "tickets:create",
    "tickets:edit",
    "assets:upload",
    "time:log",
    "time:recap",
    "projects:manage",
    "projects:templates_manage",
    "pm:client_overview",
    "pm:budget_view",
    "pm:health_view",
    "pm:capacity_view",
  ],
  training_manager: [
    // Full member baseline
    "articles:view",
    "clients:view",
    "applications:view",
    "tickets:view",
    "escalations:view",
    "assets:view",
    "onboarding:view",
    "projects:view",
    "users:view",
    "clients:manage",
    "clients:archive",
    "applications:manage",
    "articles:create",
    "feedback:create",
    "escalations:create",
    "tickets:create",
    "tickets:edit",
    "assets:upload",
    "time:log",
    "time:recap",

    // Training management permissions
    "onboarding:library_manage",
    "onboarding:own",
    "onboarding:assess",
    "training:credentials_manage",
    "onboarding:insights",
  ],
};

// Senior SE (key advanced_se, the earlier "Advanced SE" role; owner, 2026-11-04): a Junior SE who starts projects directly.
SYSTEM_ROLE_PERMISSIONS.advanced_se = [...SYSTEM_ROLE_PERMISSIONS.member, "projects:start"];
